import * as CL from '@design-system/component-library';
import { useEffect, useRef, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { fieldCantBeEmptyMsg, submitMsg, t } from '../../common/i18n/index.js';
import { TextAreaComponent } from '../../common/react-hook-form/components/TextAreaComponent.js';
import { SURVEY_SUCCESS_DISPLAY_DURATION_MS } from './constants.js';
import { getAgentFirstName } from './utils.js';
import type { ChatFeedbackData } from './types.js';

import './ChatFeedbackSurvey.scss';

interface ChatFeedbackSurveyProps {
  onSubmit: (data: ChatFeedbackData) => Promise<void>;
  onClose: () => void;
  surveyMessageId?: string;
  isBotOnly?: boolean;
  agentName?: string;
}

type SurveyQuestion = {
  id: keyof Omit<ChatFeedbackData, 'feedback' | 'surveyMessageId'>;
  question: string;
  options: number[];
  scaleLabels?: { first: string; last: string };
};

export const ChatFeedbackSurvey = ({
  onSubmit,
  onClose,
  surveyMessageId,
  isBotOnly = false,
  agentName,
}: ChatFeedbackSurveyProps) => {
  const [submitted, setSubmitted] = useState(false);
  const thankYouRef = useRef<HTMLDivElement>(null);
  const agentFirstName = getAgentFirstName(agentName);

  const methods = useForm<ChatFeedbackData>({
    defaultValues: {
      csat: undefined,
      care: undefined,
      ces: undefined,
      feedback: '',
      surveyMessageId,
    },
    mode: 'onSubmit',
  });

  const {
    watch,
    setValue,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = methods;

  const SURVEY_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
  const SURVEY_FIRST_OPTION = SURVEY_OPTIONS[0];
  const SURVEY_LAST_OPTION = SURVEY_OPTIONS[SURVEY_OPTIONS.length - 1];

  const SURVEY_QUESTIONS: SurveyQuestion[] = isBotOnly
    ? [
        {
          id: 'csat',
          question: t.U4WE('How satisfied are you with the service you received?'),
          options: SURVEY_OPTIONS,
          scaleLabels: {
            first: t.G2SA(`${SURVEY_FIRST_OPTION} = Very dissatisfied`, `${SURVEY_FIRST_OPTION}`),
            last: t.ADCR(`${SURVEY_LAST_OPTION} = Very satisfied`, `${SURVEY_LAST_OPTION}`),
          },
        },
        {
          id: 'ces',
          question: t.P0F9('How easy was it to take care of your issue?'),
          options: SURVEY_OPTIONS,
          scaleLabels: {
            first: t.LC00(`${SURVEY_FIRST_OPTION} = Very difficult`, `${SURVEY_FIRST_OPTION}`),
            last: t.N834(`${SURVEY_LAST_OPTION} = Very easy`, `${SURVEY_LAST_OPTION}`),
          },
        },
      ]
    : [
        {
          id: 'csat',
          question: t.YB6A('The service I received was friendly'),
          options: SURVEY_OPTIONS,
        },
        {
          id: 'care',
          question: t.IYN3('I felt that Elisa cares about me as a customer'),
          options: SURVEY_OPTIONS,
        },
        {
          id: 'ces',
          question: t.P0F9('How easy was it to take care of your issue?'),
          options: SURVEY_OPTIONS,
          scaleLabels: {
            first: t.LC00(`${SURVEY_FIRST_OPTION} = Very difficult`, `${SURVEY_FIRST_OPTION}`),
            last: t.N834(`${SURVEY_LAST_OPTION} = Very easy`, `${SURVEY_LAST_OPTION}`),
          },
        },
      ];

  const handleSubmitFeedback = async (data: ChatFeedbackData) => {
    // Validate required fields
    let hasErrors = false;

    if (data.csat === undefined) {
      setError('csat', { type: 'required', message: t.VPVR(fieldCantBeEmptyMsg) });
      hasErrors = true;
    }

    if (!isBotOnly && data.care === undefined) {
      setError('care', { type: 'required', message: t.VPVR(fieldCantBeEmptyMsg) });
      hasErrors = true;
    }

    if (data.ces === undefined) {
      setError('ces', { type: 'required', message: t.VPVR(fieldCantBeEmptyMsg) });
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    await onSubmit({
      ...data,
      surveyMessageId,
    });
    setSubmitted(true);
  };

  useEffect(() => {
    if (submitted) {
      thankYouRef.current?.focus();
      const timer = setTimeout(() => {
        onClose();
      }, SURVEY_SUCCESS_DISPLAY_DURATION_MS);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [submitted, onClose]);

  if (submitted) {
    return (
      <div className="chat-feedback-survey">
        <div className="chat-feedback-survey__header">
          <div className="chat-feedback-survey__logo" role="img" aria-label="Elisa" />
        </div>
        <div
          ref={thankYouRef}
          className="chat-feedback-survey__thank-you"
          role="status"
          aria-live="polite"
          aria-atomic="true"
          tabIndex={-1}
        >
          <h2 className="chat-feedback-survey__title">{t.WSE5('Thank you for your feedback!')}</h2>
          <div className="chat-feedback-survey__thank-you__logo" role="img" aria-label="Thank you" />
        </div>
      </div>
    );
  }

  return (
    <FormProvider {...methods}>
      <div className="chat-feedback-survey">
        <div className="chat-feedback-survey__header">
          <div className="chat-feedback-survey__logo" role="img" aria-label="Elisa" />
        </div>

        <form onSubmit={handleSubmit(handleSubmitFeedback)} className="chat-feedback-survey__content">
          {agentFirstName && (
            <p className="chat-feedback-survey__agent-name">{t.ISHZ('You were served by: {}', agentFirstName)}</p>
          )}
          {SURVEY_QUESTIONS.map(question => (
            <div key={question.id} className="chat-feedback-survey__question-block">
              <h2 className="chat-feedback-survey__question-title">
                {question.question} <span className="chat-feedback-survey__required">*</span>
              </h2>

              <div className="chat-feedback-survey__scale-labels">
                <p className="chat-feedback-survey__scale-label">
                  {question.scaleLabels?.first ??
                    t.AEPM(`${SURVEY_FIRST_OPTION} = Fully disagree`, `${SURVEY_FIRST_OPTION}`)}
                </p>
                <p className="chat-feedback-survey__scale-label">
                  {question.scaleLabels?.last ?? t.XJKE(`${SURVEY_LAST_OPTION} = Fully agree`, `${SURVEY_LAST_OPTION}`)}
                </p>
              </div>

              <div
                className="chat-feedback-survey__options"
                role="radiogroup"
                aria-label={question.question}
                aria-required="true"
                aria-invalid={!!errors[question.id]}
              >
                {question.options.map(value => {
                  const isSelected = watch(question.id) === value;
                  return (
                    <button
                      key={value}
                      type="button"
                      role="radio"
                      aria-checked={isSelected}
                      className={`chat-feedback-survey__option ${
                        isSelected ? 'chat-feedback-survey__option--selected' : ''
                      }`}
                      onClick={() => {
                        setValue(question.id, value);
                        clearErrors(question.id);
                      }}
                    >
                      {value}
                    </button>
                  );
                })}
              </div>
              {errors[question.id] && (
                <p className="chat-feedback-survey__error" role="alert">
                  {t.VPVR(fieldCantBeEmptyMsg)}
                </p>
              )}
            </div>
          ))}

          <h2 className="chat-feedback-survey__question-title">
            {t.YXNW('If you wish, you can also provide open feedback:')}
          </h2>
          <TextAreaComponent rows={4} maxLength={4000} disabled={false} name="feedback" />

          <div className="chat-feedback-survey__actions">
            <CL.Button type="submit">{t.FSWU(submitMsg)}</CL.Button>
          </div>
        </form>
      </div>
    </FormProvider>
  );
};
